// The customer quote part of a new enquiry: customer, contact, product type, branch and the customer check (D5, decision
// 18/10). Shared by design A (BidEditor, step 4) and design B (NewEnquiryClassic, one dialog); the component gives
// sharedEnqId (null = the job is not saved yet, so nothing can clash), settings and onlyUserBranch.
import api from './sharedEnquiryApi'
import { CHANCES, checkDisplay, missingMandatory, errorText, isBlank } from './sharedEnquiryLogic'
import { existingQuotes } from './customerQuotes'

export default {
  data() {
    return {
      customer: null, contact: null, phoneNo: '', email: '', productTypeId: null, branchId: null, projectTypeId: null,
      chance: null, branchMargin: null, chaserUserId: null,
      customerSuggestions: [], contacts: [], branches: [], productTypes: [], projectTypes: [], users: [],
      chances: CHANCES, branchesLoaded: false, questionOpen: false, check: null, checking: false, confirmed: false, submitted: false, saving: false, error: '', checkSeq: 0
    }
  },
  computed: {
    // Designs B and D (Mohammed 18/10): the duplicate question is asked up front, in a modal over the form, once the
    // customer, the product type and the branch are chosen; A and C show the check beside the fields instead
    askUpFront() { return false },
    customerId() { return this.customer && typeof this.customer === 'object' ? this.customer.customerId : null },
    branch() { return this.branches.find(b => b.branchId === this.branchId) || null },
    salesExecName() { return this.branch ? this.branch.salesExecName : '' },
    noOwnBranch() { return this.onlyUserBranch && !!this.customerId && this.branchesLoaded && !this.branchId },
    body() {
      return {
        customerId: this.customerId, contactId: this.contact ? this.contact.contactId : null, phoneNo: this.phoneNo || null, email: this.email || null,
        productTypeId: this.productTypeId, branchId: this.branchId, projectTypeId: this.projectTypeId,
        salesExecId: this.branch ? this.branch.salesExecId || null : null,
        chance: this.chance, branchMargin: this.branchMargin, chaserUserId: this.chaserUserId
      }
    },
    bidSettings() { return this.settings.filter(s => s.level === 'bid') },
    missing() { return missingMandatory(this.bidSettings, { bids: [this.body] }) },
    display() { return checkDisplay(this.check, this.confirmed, this.customer && this.customer.custName) },
    existing() { return existingQuotes(this.check) },
    // BLOCK, an unconfirmed WARN, or a check still running: no new customer quote. Missing fields are listed on click.
    blocked() { return this.checking || this.noOwnBranch || this.display.severity === 'error' || this.display.needsConfirm },
    // "New Customer Quote" is offered only when the rule allows it: no clash, or a WARN once confirmed
    canOffer() { return !this.blocked }
  },
  watch: {
    customerId() { this.runCheck() },
    productTypeId() { this.runCheck() },
    // the rule depends on the branch too (same branch = BLOCK, another branch = WARN)
    branchId() { this.runCheck() }
  },
  async mounted() {
    const load = async (key, fn) => {
      try { this[key] = (await fn()) || [] } catch (e) { this.error = 'Some lists did not load (' + key + ').' }
    }
    await Promise.all([load('productTypes', api.productTypes), load('projectTypes', api.projectTypes), load('users', api.users)])
    if (this.initialCustomer && this.initialCustomer.customerId) this.useInitialCustomer()
  },
  methods: {
    // the CRM's normal revision of that quote (same quote number, next version), on the enquiry's Quotations tab
    openNewVersion(e) {
      if (e.route && this.$router) this.$router.push(e.route)
    },
    req(key) { return this.bidSettings.some(s => s.fieldKey === key && s.mandatory) },
    shown(key) { return this.submitted && this.req(key) && isBlank(this.body[key]) },
    async findCustomers(q) {
      try {
        this.customerSuggestions = ((await api.searchCustomers(q)) || []).slice(0, 30)
      } catch (e) {
        this.customerSuggestions = []
      }
    },
    useInitialCustomer() {
      this.customer = { ...this.initialCustomer }
      this.onCustomer(this.customer)
    },
    async onCustomer(c) {
      this.contact = null
      this.contacts = []
      this.branches = []
      this.branchId = null
      this.branchesLoaded = false
      if (!c || !c.customerId) return
      try {
        const [contacts, branches] = await Promise.all([api.contacts(c.customerId), api.branches(c.customerId)])
        this.contacts = contacts || []
        this.branches = branches || []
        this.branchesLoaded = true
        if (this.onlyUserBranch) {
          // as the old form: only the user's own branch, and only if the customer has it
          const profile = this.$store && this.$store.getters['auth/getMyProfile']
          const own = profile && profile.defBranch
          const mine = own ? this.branches.filter(b => b.branchId == own.id) : []
          this.branchId = mine.length === 1 ? mine[0].branchId : null
        } else if (this.branches.length === 1) {
          this.branchId = this.branches[0].branchId
        }
      } catch (e) {
        this.error = errorText(e, 'Could not load the customer\'s contacts and branches')
      }
    },
    onContact() {
      const c = this.contact
      if (!c) return
      // the CRM stores "-" for an unknown number
      const real = v => (v && String(v).trim() !== '-' ? v : '')
      if (!this.phoneNo) this.phoneNo = real(c.phoneNo) || real(c.mobile)
      if (!this.email) this.email = real(c.email)
    },
    // D5: checked as soon as both the customer and the product type are chosen
    async runCheck() {
      this.check = null
      this.confirmed = false
      this.questionOpen = false
      if (!this.customerId || !this.productTypeId || (this.askUpFront && !this.branchId)) return
      // no job saved yet: there is nothing on it to clash with
      if (!this.sharedEnqId) {
        this.check = { result: 'OK', message: '', existing: [] }
        return
      }
      const seq = ++this.checkSeq
      this.checking = true
      try {
        const r = await api.checkCustomer(this.sharedEnqId, this.customerId, this.productTypeId, this.branchId)
        if (seq === this.checkSeq) this.useCheck(r)
      } catch (e) {
        if (seq === this.checkSeq) this.error = errorText(e, 'Could not check this customer against the enquiry')
      } finally {
        if (seq === this.checkSeq) this.checking = false
      }
    },
    async add() {
      this.submitted = true
      this.error = ''
      if (this.missing.length || this.blocked) return
      if (!this.check) await this.runCheck()
      if (!this.check) {
        this.error = this.error || (this.askUpFront ? 'Choose the customer, the product type and the branch first.' : 'Choose the customer and the product type first.')
        return
      }
      if (!this.display.canAdd) return
      this.saving = true
      try {
        const opp = await api.addBid(this.sharedEnqId, { ...this.body, confirmWarning: this.confirmed || undefined })
        this.bidAdded(opp)
      } catch (e) {
        // the CRM's axios handler rejects with undefined when there is no readable response (network, CORS, 503)
        const r = e && e.response
        const d = r && r.data
        if (r && r.status === 409 && d && d.guard === 'database') {
          // the database rule refused it (someone added the same quote meanwhile): its body has no list, so re-check
          this.error = 'Not added: ' + errorText(e)
          this.runCheck()
        } else if (r && r.status === 409 && d && d.result) {
          // the server re-checked D5 and found something since: show its check in place of ours
          this.confirmed = false
          this.useCheck(d)
          this.error = 'Not added: ' + errorText(e)
        } else if (r && r.status === 409) {
          this.error = 'Not added: ' + errorText(e)
          this.runCheck()
        } else if (!r) {
          // no answer (timeout, restart, network): the save may still have happened on the server, so the
          // enquiry is reloaded and the customer re-checked before anyone tries again (a saved bid now shows as BLOCK)
          this.error = 'No answer from the server, so the customer may have been added. The list has been reloaded: check it before trying again.'
          this.$emit('uncertain')
          this.runCheck()
        } else {
          this.error = errorText(e, 'Could not add the customer')
        }
      } finally {
        this.saving = false
      }
    },
    useCheck(check) {
      this.check = check
      this.questionOpen = this.askUpFront && !!check && !!check.result && check.result !== 'OK'
    },
    // the answers to the up-front question
    answerNewVersion(e) {
      this.questionOpen = false
      this.openNewVersion(e)
    },
    answerAnyway() {
      this.confirmed = true
      this.questionOpen = false
    },
    answerChangeCustomer() {
      this.questionOpen = false
      this.checkSeq++
      Object.assign(this, { customer: null, contact: null, contacts: [], branches: [], branchId: null, branchesLoaded: false, check: null, confirmed: false })
    },
    bidAdded(opp) {
      this.$emit('added', opp)
      this.reset()
    },
    reset() {
      Object.assign(this, {
        customer: null, contact: null, phoneNo: '', email: '', productTypeId: null, branchId: null, projectTypeId: null,
        chance: null, branchMargin: null, chaserUserId: null, contacts: [], branches: [], branchesLoaded: false,
        check: null, confirmed: false, submitted: false, questionOpen: false
      })
    }
  }
}
